import React from "react";
import { Link } from "react-router-dom";
import { HeartPulse, Cpu, Palette, Scale, TrendingUp, ArrowUpRight, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import { Logo } from "../components/Logo";

const PILLARS = [
  {
    key: "healthcare",
    label: "Healthcare",
    Icon: HeartPulse,
    blurb:
      "Physical and mental flourishing — sleep, movement, regulation, and the well-being of those around us.",
    accent: "#10B981",
  },
  {
    key: "technology",
    label: "Technology",
    Icon: Cpu,
    blurb:
      "Fluency, agency, and ethics in a world being rewritten by software, algorithms, and AI.",
    accent: "#06B6D4",
  },
  {
    key: "arts",
    label: "Arts & Culture",
    Icon: Palette,
    blurb:
      "Creativity, taste, heritage, and the courage to make things — the soul of a civilisation.",
    accent: "#F59E0B",
  },
  {
    key: "law",
    label: "Law & Governance",
    Icon: Scale,
    blurb:
      "Rights, responsibilities, and the integrity to participate in the systems that shape us.",
    accent: "#4F46E5",
  },
  {
    key: "commerce",
    label: "Commerce & Economy",
    Icon: TrendingUp,
    blurb:
      "Financial literacy, ethical exchange, and a sustainable relationship with money and value.",
    accent: "#64748B",
  },
];

const TIERS = [
  { range: "1 – 3", title: "Foundational Awareness", body: "Basic knowledge, limited access." },
  { range: "4 – 6", title: "Developing Proficiency", body: "Engagement, active learning, growing fluency." },
  { range: "7 – 8", title: "Applied Competence", body: "Confident use, ethical decision-making, cross-domain synthesis." },
  { range: "9 – 10", title: "Mastery & Impact", body: "Teaching, leading, innovating, system-level influence." },
];

export default function Landing() {
  return (
    <div className="App" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative border-b border-[var(--es-line)] overflow-hidden">
        <div className="es-grain absolute inset-0 opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-32 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end">
            <div className="md:col-span-7">
              <p
                className="font-ui text-xs tracking-[0.28em] uppercase text-[var(--es-blue)] mb-6"
                data-testid="hero-overline"
              >
                A new measure of human progress · Est. 2025
              </p>
              <h1
                className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[var(--es-ink)] tracking-tight"
                data-testid="hero-title"
              >
                Beyond <span className="italic">IQ</span>. Beyond <span className="italic">GDP</span>.
                <br />
                Measure who you <em className="text-[var(--es-blue)] not-italic" style={{ fontStyle: "italic" }}>actually</em> are.
              </h1>
              <p
                className="mt-8 font-body text-lg md:text-xl text-[var(--es-ink-soft)] max-w-2xl leading-relaxed"
                data-testid="hero-subtitle"
              >
                The Erudition Score is a holistic 0–10 reading of your capacity across five
                dimensions of modern life — health, technology, culture, governance, and economy.
                One score. Five pillars. A clearer mirror of who you are becoming.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 items-center">
                <Link
                  to="/quiz"
                  className="bg-[var(--es-blue)] text-white px-8 py-4 hover:bg-[var(--es-blue-hover)] transition-colors uppercase tracking-wider text-sm font-semibold font-ui inline-flex items-center gap-2"
                  data-testid="hero-cta-take-assessment"
                >
                  Take the Assessment <ArrowUpRight size={16} strokeWidth={1.8} />
                </Link>
                <Link
                  to="#methodology"
                  className="es-link-underline font-ui text-sm uppercase tracking-wider text-[var(--es-ink-soft)] hover:text-[var(--es-blue)]"
                  data-testid="hero-cta-methodology"
                >
                  How it works
                </Link>
              </div>
              <div className="mt-12 flex items-center gap-6 text-xs uppercase tracking-widest font-ui text-[var(--es-ink-soft)]">
                <span data-testid="hero-stat-questions">45 questions</span>
                <span className="w-1 h-1 bg-[var(--es-line)]" />
                <span data-testid="hero-stat-time">~7 minutes</span>
                <span className="w-1 h-1 bg-[var(--es-line)]" />
                <span data-testid="hero-stat-pillars">5 pillars</span>
              </div>
            </div>

            <div className="md:col-span-5 relative">
              <div className="aspect-square bg-[var(--es-blue)] flex items-center justify-center relative">
                <Logo size={320} variant="mark" className="z-10" />
                <div className="absolute inset-0 border-[14px] border-white/10 mix-blend-overlay" />
              </div>
              <div className="grid grid-cols-2 gap-0 mt-0 border-t-0">
                <div className="border border-t-0 border-[var(--es-line)] p-5 bg-white">
                  <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)]">
                    Sample score
                  </p>
                  <p className="font-display text-4xl text-[var(--es-ink)] mt-1">7.4</p>
                  <p className="font-ui text-xs uppercase tracking-wider text-[var(--es-blue)] mt-1">
                    Applied Competence
                  </p>
                </div>
                <div className="border border-t-0 border-l-0 border-[var(--es-line)] p-5 bg-[var(--es-bg-soft)]">
                  <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)]">
                    Strongest pillar
                  </p>
                  <p className="font-display text-2xl text-[var(--es-ink)] mt-1 leading-tight">
                    Arts & Culture
                  </p>
                  <p className="font-ui text-xs uppercase tracking-wider text-[var(--es-ink-soft)] mt-1">
                    8.6 / 10
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FIVE PILLARS */}
      <section className="border-b border-[var(--es-line)] bg-[var(--es-bg-soft)]" id="pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <p className="font-ui text-xs tracking-[0.28em] uppercase text-[var(--es-blue)] mb-4">
                The framework
              </p>
              <h2 className="font-display text-4xl sm:text-5xl text-[var(--es-ink)] tracking-tight">
                Five pillars of <em className="text-[var(--es-blue)]">human</em> capacity
              </h2>
            </div>
            <p className="font-body text-[var(--es-ink-soft)] max-w-md leading-relaxed">
              Literacy is no longer enough. Modern life asks of us a richer, more multidimensional
              fluency. The Erudition Score reads each of these dimensions with care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--es-line)] border border-[var(--es-line)]">
            {PILLARS.map(({ key, label, Icon, blurb, accent }, i) => (
              <article
                key={key}
                className="bg-white p-8 md:p-10 group hover:bg-[var(--es-blue-soft)] transition-colors duration-300"
                data-testid={`pillar-card-${key}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div
                    className="w-12 h-12 flex items-center justify-center"
                    style={{ background: accent + "1A", color: accent }}
                  >
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <span className="font-ui text-xs tracking-widest uppercase text-[var(--es-ink-soft)]">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-2xl text-[var(--es-ink)] mb-3">{label}</h3>
                <p className="font-body text-[var(--es-ink-soft)] leading-relaxed">{blurb}</p>
              </article>
            ))}
            <article className="bg-[var(--es-dark)] p-8 md:p-10 text-white flex flex-col justify-between">
              <div>
                <p className="font-ui text-[11px] tracking-[0.28em] uppercase text-white/60 mb-4">
                  The output
                </p>
                <h3 className="font-display text-3xl mb-3">
                  One number, <em className="text-white">five</em> truths.
                </h3>
                <p className="font-body text-white/70 leading-relaxed">
                  Your Erudition Score is the average of all five pillars — but the real story lives
                  in the distribution.
                </p>
              </div>
              <Link
                to="/quiz"
                className="mt-8 font-ui text-xs uppercase tracking-widest inline-flex items-center gap-2 group/link"
                data-testid="pillar-card-cta"
              >
                Begin assessment{" "}
                <ArrowUpRight size={14} strokeWidth={1.6} className="group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
              </Link>
            </article>
          </div>
        </div>
      </section>

      {/* TIER LADDER */}
      <section className="border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-4">
              <p className="font-ui text-xs tracking-[0.28em] uppercase text-[var(--es-blue)] mb-4">
                The scale
              </p>
              <h2 className="font-display text-4xl sm:text-5xl text-[var(--es-ink)] tracking-tight leading-[1.05]">
                A ladder, <br /> not a label.
              </h2>
              <p className="font-body text-[var(--es-ink-soft)] mt-6 leading-relaxed">
                Every score is a starting point. The tiers describe how knowledge is held — from
                first contact to mastery, where you teach, lead and shape systems.
              </p>
            </div>
            <ol className="md:col-span-8 space-y-0">
              {TIERS.map((t, i) => (
                <li
                  key={t.range}
                  className="border-t border-[var(--es-line)] py-8 grid grid-cols-12 gap-4 items-baseline group hover:bg-[var(--es-bg-soft)] transition-colors px-2"
                  data-testid={`tier-row-${i}`}
                >
                  <span className="col-span-2 font-ui text-xs tracking-widest uppercase text-[var(--es-ink-soft)]">
                    Tier {i + 1}
                  </span>
                  <span className="col-span-2 font-display text-2xl text-[var(--es-blue)]">
                    {t.range}
                  </span>
                  <span className="col-span-8 md:col-span-3 font-display text-xl text-[var(--es-ink)]">
                    {t.title}
                  </span>
                  <span className="col-span-12 md:col-span-5 font-body text-[var(--es-ink-soft)]">
                    {t.body}
                  </span>
                </li>
              ))}
              <li className="border-t border-b border-[var(--es-line)]" />
            </ol>
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section
        id="methodology"
        className="border-b border-[var(--es-line)] bg-[var(--es-dark)] text-white relative overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-30 mix-blend-overlay"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/53782/pexels-photo-53782.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--es-dark)] via-[var(--es-dark)]/90 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <p className="font-ui text-xs tracking-[0.28em] uppercase text-white/60 mb-4">
                Methodology
              </p>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                Built on the shoulders of <em className="text-[#9CB6E5]">global</em> indices.
              </h2>
              <p className="font-body text-white/70 mt-6 leading-relaxed max-w-2xl">
                The Erudition Score draws from established frameworks — HDI, the Global Innovation
                Index, Worldwide Governance Indicators, and others — and synthesises them with new
                qualitative metrics for a fuller, more humane reading of progress.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 mt-12 border border-white/10">
                {[
                  { k: "Quantitative", v: "Standardised data, outcomes, indices" },
                  { k: "Qualitative", v: "Engagement, ethics, creativity" },
                  { k: "Adaptive", v: "Weights tuned to context & stage" },
                  { k: "Inclusive", v: "Individuals, organisations, nations" },
                  { k: "Actionable", v: "Score → reflection → growth plan" },
                  { k: "Future-aware", v: "Built for an AI, climate-pressed era" },
                ].map((c) => (
                  <div key={c.k} className="bg-[var(--es-dark)] p-5">
                    <p className="font-ui text-[11px] uppercase tracking-widest text-white/50">
                      {c.k}
                    </p>
                    <p className="font-display text-lg mt-1 leading-snug">{c.v}</p>
                  </div>
                ))}
              </div>
            </div>
            <aside className="md:col-span-5 md:pl-8 md:border-l md:border-white/10">
              <p className="font-display text-3xl leading-snug">
                "Literacy alone won't equip a person for the 21st century. We need a richer
                cartography of human flourishing."
              </p>
              <p className="font-ui text-xs uppercase tracking-widest text-white/50 mt-6">
                — From the Erudition Score Book
              </p>
            </aside>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Sparkles size={20} strokeWidth={1.5} className="text-[var(--es-blue)] mb-4" />
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--es-ink)] tracking-tight leading-[1.05]">
              Discover where you stand. <br />
              Then design where you go next.
            </h2>
            <p className="font-body text-[var(--es-ink-soft)] mt-6 max-w-xl leading-relaxed">
              Your Erudition Score arrives with a personalised, AI-crafted growth plan — anchored
              to your strengths and tuned to your blind spots.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/quiz"
              className="bg-[var(--es-blue)] text-white px-8 py-4 hover:bg-[var(--es-blue-hover)] transition-colors uppercase tracking-wider text-sm font-semibold font-ui inline-flex items-center gap-2"
              data-testid="footer-cta-take-assessment"
            >
              Take the Assessment <ArrowUpRight size={16} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-ui text-[var(--es-ink-soft)] uppercase tracking-widest">
          <p>© {new Date().getFullYear()} Mahakali Tribunal · A holistic measure of human progress</p>
          <p>Healthcare · Technology · Arts · Governance · Commerce</p>
        </div>
      </footer>
    </div>
  );
}
